import type { MeetingPrepMessagePart } from "@acto/genui-components-schema";
import { create } from "zustand";

export const MEETING_PREP_TTL_MS = 5 * 60 * 1000;

export type MeetingPrepStatus = "idle" | "streaming" | "refreshing" | "succeeded" | "failed";

export interface MeetingPrepEntry {
  parts: MeetingPrepMessagePart[];
  /** Session id of the generated prep the parts came from; per-card hides are scoped to it. */
  sessionId: string | null;
  /** ISO 8601 time the intelligence layer generated the prep; null when it did not report one. */
  generatedAt: string | null;
  status: MeetingPrepStatus;
  error: Error | null;
  fetchedAt: number;
  controller: AbortController | null;
}

export const EMPTY_MEETING_PREP_ENTRY: MeetingPrepEntry = {
  parts: [],
  sessionId: null,
  generatedAt: null,
  status: "idle",
  error: null,
  fetchedAt: 0,
  controller: null,
};

interface MeetingPrepStore {
  byKey: Map<string, MeetingPrepEntry>;
  update: (key: string, patch: Partial<MeetingPrepEntry>) => void;
}

export const getMeetingPrepCacheKey = (externalReference: string, locale: string): string =>
  `${externalReference}:${locale}`;

export const isFresh = (entry: MeetingPrepEntry, now = Date.now()): boolean =>
  entry.fetchedAt > 0 && now - entry.fetchedAt < MEETING_PREP_TTL_MS;

export const isInFlight = (entry: MeetingPrepEntry): boolean =>
  entry.status === "streaming" || entry.status === "refreshing";

export const useMeetingPrepStore = create<MeetingPrepStore>((set) => ({
  byKey: new Map(),
  update: (key, patch) =>
    set((state) => {
      const next = new Map(state.byKey);
      next.set(key, { ...(next.get(key) ?? EMPTY_MEETING_PREP_ENTRY), ...patch });
      return { byKey: next };
    }),
}));
